import React, { useState, useEffect, useCallback, useRef } from "react";
import dayjs from "dayjs";

import IconButton from "@mui/material/IconButton";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SkipNextIcon from "@mui/icons-material/SkipNext";

import {
  PALETTE,
  PLAYTIME_PHASE,
  DISCUSS_PARTNER,
  TIMER_MODAL_SUBPHASE,
  isNavKey,
  DIRECTION,
  EDITING_STATE,
} from "../constants";
import TimerModal from "./TimerModal";
import TimerDisplay from "./TimerDisplay";
import { sounds } from "../assets";
import { TEACH_EVENT_LOG_TYPES } from "../hooks/useTeachEventLogger";

const SECONDS_IN_MINUTE = 60;
const { LOGIN, EXPLORE, KEEPGOING, FREEPLAY } = PLAYTIME_PHASE;
const DEFAULT_TIMES_BY_PHASE = {
  [LOGIN]: 3,
  [EXPLORE]: 4,
  [DISCUSS_PARTNER]: 4,
  [KEEPGOING]: 26,
  [FREEPLAY]: 0,
};

const DEFAULT_ICON_FONT_SIZE = "24px";
const ICON_BUTTON_BG_COLOR = "#616161";

const { START, END, END_EARLY } = TIMER_MODAL_SUBPHASE;
const { PREV, NEXT } = DIRECTION;

const {
  TIMER_CHANGE,
  TIMER_END,
  TIMER_END_EARLY,
  TIMER_START_STUDENT_GAMEPLAY,
} = TEACH_EVENT_LOG_TYPES;

// used to disable input when transitioning from active gameplay to end timer modal
const KEYDOWN_DISABLED_DURATION = 2000;

const { CANCEL_EDIT_QUEUED, CONFIRM_EDIT_QUEUED, IS_EDITING } = EDITING_STATE;

const { notificationSound } = sounds;

// timer progress statuses
const STATUS = {
  UNSTARTED: "TIMER_NOT_STARTED",
  STARTED: "TIMER_STARTED",
  ENDED: "TIMER_ENDED",
};

// timer times and default times are in minutes and logic to determine time remaining is in seconds
const Timer = ({
  timerTimes = {},
  phase,
  type,
  setDisableArrowControls,
  handleNavigation,
  nextSlideLabel,
  timerModalInfo,
  setTimerModalInfo,
  isGamePlaying,
  setIsGamePlaying,
  startTimerModalCallback,
  isTutorialTooltipBlockingInputs,
  completeActiveTutorial,
  isOnFirstSlide,
  dispatchTeachEventLog,
  setOnFreeplayHandler,
}) => {
  const defaultTimerDuration =
    (timerTimes[phase] ?? DEFAULT_TIMES_BY_PHASE[phase]) * SECONDS_IN_MINUTE;

  const [progressStatus, setProgressStatus] = useState(STATUS.UNSTARTED);
  const [isPaused, setIsPaused] = useState(true);

  const notificationSoundRef = useRef();

  const [timerDuration, setTimerDuration] = useState(defaultTimerDuration);
  const [timeRemaining, setTimeRemaining] = useState(timerDuration);

  const [disableKeydown, setDisableKeydown] = useState(false);

  const [editingState, setEditingState] = useState(null);
  useEffect(() => {
    // prevents timer from starting while modal is open
    if (timerModalInfo.isVisible) {
      return;
    } else if (editingState === CANCEL_EDIT_QUEUED) {
      setIsPaused(false);
      setEditingState(null);
    } else if (editingState === CONFIRM_EDIT_QUEUED) {
      const deltaTime = timerDuration - timeRemaining;
      if (deltaTime) {
        dispatchTeachEventLog(TIMER_CHANGE, {
          meta_string: dayjs.unix(timeRemaining).format("mm:ss"),
          meta_int: deltaTime,
        });

        // if user updates timer, end freeplay and start timer
        setOnFreeplayHandler?.(null);
        setProgressStatus(STATUS.STARTED);
      }

      setTimeRemaining(timerDuration);
      setIsPaused(false);
      setEditingState(null);
    }
  }, [
    editingState,
    timerDuration,
    timerModalInfo,
    dispatchTeachEventLog,
    timeRemaining,
    setOnFreeplayHandler,
  ]);

  // FREEPLAY is a special phase that always has an END_EARLY subphase
  // FREEPLAY timer is never started and has no remaining time which is why the
  // check for FREEPLAY must come first
  const modalSubphase =
    phase === FREEPLAY
      ? END_EARLY
      : progressStatus === STATUS.UNSTARTED
      ? START
      : timeRemaining
      ? END_EARLY
      : END;

  const handleTogglePlay = () => {
    if (!timeRemaining) {
      return;
    }
    setIsPaused(!isPaused);
  };

  const handleCloseModal = useCallback(
    (e) => {
      if (disableKeydown) {
        return;
      }

      const isPrevNavKey = isNavKey(PREV, e.key);
      const isNextNavKey = isNavKey(NEXT, e.key);

      if (
        e.key &&
        // ignore prev nav keypress on first slide unless end early modal is showing
        !(isPrevNavKey && (!isOnFirstSlide || modalSubphase === END_EARLY)) &&
        !isNextNavKey &&
        e.key !== "Escape"
      ) {
        return;
      }

      const { endTimerEarlyDirection } = timerModalInfo;
      if (phase !== FREEPLAY) {
        setIsPaused(false);
      }

      setTimerModalInfo({
        isVisible: false,
        endTimerEarlyDirection: null,
      });

      if (modalSubphase === START) {
        setProgressStatus(STATUS.STARTED);
        if (type === "game" && !isPrevNavKey) {
          startTimerModalCallback?.();
          dispatchTeachEventLog(TIMER_START_STUDENT_GAMEPLAY);
        }
      }

      if (modalSubphase === END_EARLY) {
        // always log event regardless of whether teacher closes modal to stay
        // on slide or navigates to the prev/next
        dispatchTeachEventLog(TIMER_END_EARLY, { meta_int: timeRemaining });
      }

      // timer keyboard nav maps to back/continue button on timer modal (END EARLY modal subphase)
      // if timer only have one button, always be able to navigation to previous slide
      // only navigate to next slide if modal subphase is END.
      if (
        (modalSubphase !== END_EARLY && isPrevNavKey) ||
        (modalSubphase === END_EARLY &&
          endTimerEarlyDirection === PREV &&
          (e.target.id === "timer-modal-prev-btn" || isPrevNavKey))
      ) {
        handleNavigation(PREV);
      } else if (
        modalSubphase === END ||
        (modalSubphase === END_EARLY &&
          endTimerEarlyDirection === NEXT &&
          (e.target.id === "timer-modal-next-btn" || isNextNavKey))
      ) {
        handleNavigation(NEXT);
      }
    },
    [
      phase,
      handleNavigation,
      modalSubphase,
      setTimerModalInfo,
      disableKeydown,
      startTimerModalCallback,
      type,
      timerModalInfo,
      isOnFirstSlide,
      dispatchTeachEventLog,
      timeRemaining,
    ]
  );

  const handleEarlyNavigation = useCallback(
    (e) => {
      if (editingState || isTutorialTooltipBlockingInputs) {
        return;
      }
      if (
        modalSubphase === END_EARLY &&
        ((isNavKey("prev", e.key) && !isOnFirstSlide) ||
          isNavKey("next", e.key) ||
          e.type === "click") // e.type will only be click when clicking on skip to end button, otherwise e.type will be keydown
      ) {
        setTimerModalInfo({
          isVisible: true,
          endTimerEarlyDirection: isNavKey(PREV, e.key) ? PREV : NEXT,
        });
        setIsPaused(true);
      }
    },
    [
      modalSubphase,
      setTimerModalInfo,
      editingState,
      isTutorialTooltipBlockingInputs,
      isOnFirstSlide,
    ]
  );

  useEffect(() => {
    const timerInterval = setInterval(() => {
      if (isPaused || !timeRemaining) {
        return;
      }
      setTimeRemaining((prevTime) => {
        if (prevTime === 0) {
          return 0;
        } else {
          return prevTime - 1;
        }
      });
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [isPaused, timeRemaining]);

  // dispatch the timer end event ONLY ONCE
  // (ensured by checking and setting progressStatus)
  useEffect(() => {
    if (!timeRemaining && progressStatus === STATUS.STARTED) {
      dispatchTeachEventLog(TIMER_END);
      setProgressStatus(STATUS.ENDED);
    }
  }, [dispatchTeachEventLog, progressStatus, timeRemaining]);

  useEffect(() => {
    if (modalSubphase === END) {
      setTimerModalInfo({ isVisible: true, endTimerEarlyDirection: null });
      setTimeout(
        () => setDisableKeydown(false),
        isGamePlaying ? KEYDOWN_DISABLED_DURATION : 0
      );
      if (isGamePlaying) {
        setDisableKeydown(true);
        window.focus(); // take focus away from game to capture keydown
      }
      // tutorial tooltip was being shown
      if (isTutorialTooltipBlockingInputs) {
        completeActiveTutorial();
      }
      // only play notification sound on end for timer with type game
      if (type === "game") {
        notificationSoundRef.current?.play();
      }
    }
  }, [
    modalSubphase,
    setTimerModalInfo,
    isGamePlaying,
    setDisableKeydown,
    type,
    isTutorialTooltipBlockingInputs,
    completeActiveTutorial,
  ]);

  // setTimerModalInfo can be called by <Controls />
  useEffect(() => {
    if (timerModalInfo.isVisible) {
      setIsPaused(true);
    }
  }, [timerModalInfo]);

  // disable arrow controls on mount and re-enable arrow controls on unmount
  useEffect(() => {
    setDisableArrowControls(true);
    return () => {
      setDisableArrowControls(false);
    };
  }, [setDisableArrowControls]);

  useEffect(() => {
    if (phase === FREEPLAY) {
      startTimerModalCallback?.();
    } else if (modalSubphase === START && progressStatus === STATUS.UNSTARTED) {
      setTimerModalInfo({ isVisible: true, endTimerEarlyDirection: null });
    }
  }, [
    phase,
    startTimerModalCallback,
    modalSubphase,
    progressStatus,
    setTimerModalInfo,
  ]);

  useEffect(() => {
    const timerKeydownListener = (e) => {
      if (timerModalInfo.isVisible) {
        handleCloseModal(e);
      } else {
        handleEarlyNavigation(e);
      }
    };

    document.addEventListener("keydown", timerKeydownListener);

    return () => {
      document.removeEventListener("keydown", timerKeydownListener);
    };
  }, [timerModalInfo, handleCloseModal, handleEarlyNavigation]);

  // reset timerModalInfo on unmount (handles progress slider changes)
  useEffect(() => {
    return () =>
      void setTimerModalInfo({
        isVisible: false,
        endTimerEarlyDirection: null,
      });
  }, [setTimerModalInfo]);

  return (
    <div id={`timer-${type}`}>
      {type === "discuss" && (
        <TogglePlay isPaused={isPaused} handleTogglePlay={handleTogglePlay} />
      )}
      <TimerDisplay
        time={editingState ? timerDuration : timeRemaining}
        setIsPaused={setIsPaused}
        setEditingState={setEditingState}
        setIsGamePlaying={setIsGamePlaying}
        setTimerDuration={setTimerDuration}
        timerType={type}
        showTextDecoration={
          editingState === IS_EDITING && !timerModalInfo.isVisible
        }
        disableEditing={false}
      />
      {type === "discuss" && (
        <SkipToEnd handleEarlyNavigation={handleEarlyNavigation} />
      )}
      {type === "game" && (
        <TimerControlsGame
          phase={phase}
          isPaused={isPaused}
          handleTogglePlay={handleTogglePlay}
          handleEarlyNavigation={handleEarlyNavigation}
          fontSize="36px"
        />
      )}
      <TimerModal
        open={timerModalInfo.isVisible}
        handleClose={handleCloseModal}
        phase={phase}
        subphase={modalSubphase}
        continueDisabled={disableKeydown}
        time={editingState ? timerDuration : timeRemaining}
        nextSlideLabel={nextSlideLabel}
        endTimerEarlyDirection={timerModalInfo.endTimerEarlyDirection}
        setIsPaused={setIsPaused}
        setEditingState={setEditingState}
        setTimerDuration={setTimerDuration}
        setIsGamePlaying={setIsGamePlaying}
        showTextDecoration={
          editingState === IS_EDITING && modalSubphase === START
        }
      />
      {type === "game" && (
        <audio ref={notificationSoundRef} src={notificationSound} />
      )}
    </div>
  );
};

export default Timer;

const TimerControlsGame = ({
  isPaused,
  handleTogglePlay,
  handleEarlyNavigation,
  fontSize,
}) => {
  return (
    <div id="timer-game-controls">
      <TogglePlay
        isPaused={isPaused}
        handleTogglePlay={handleTogglePlay}
        fontSize={fontSize}
      />
      <SkipToEnd
        handleEarlyNavigation={handleEarlyNavigation}
        fontSize={fontSize}
      />
    </div>
  );
};

const iconButtonStyle = {
  "& .MuiSvgIcon-root": {
    color: PALETTE.gray,
  },
  "&:hover": {
    backgroundColor: ICON_BUTTON_BG_COLOR,
    "& .MuiSvgIcon-root": {
      color: "white",
    },
  },
};

const TogglePlay = ({ isPaused, handleTogglePlay, fontSize }) => {
  const iconStyle = { fontSize: fontSize ?? DEFAULT_ICON_FONT_SIZE };
  return (
    <IconButton
      onClick={handleTogglePlay}
      sx={
        isPaused
          ? {
              "&:hover": {
                backgroundColor: PALETTE.contrastGreen,
              },
              backgroundColor: PALETTE.green,
            }
          : iconButtonStyle
      }
    >
      {isPaused ? (
        <PlayArrowIcon sx={iconStyle} />
      ) : (
        <PauseIcon sx={iconStyle} />
      )}
    </IconButton>
  );
};

const SkipToEnd = ({ handleEarlyNavigation, fontSize }) => {
  return (
    <IconButton onClick={handleEarlyNavigation} sx={iconButtonStyle}>
      <SkipNextIcon sx={{ fontSize: fontSize ?? DEFAULT_ICON_FONT_SIZE }} />
    </IconButton>
  );
};
